import { Box, TextareaAutosize, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button } from 'c1ui'
import { UserAvatar } from 'components/core/avatar'
import { TextField } from 'components/core/textfield'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { useAlert } from 'hooks/useAlert/index'
import { MetaSynchronousHeader } from 'lib/constants/platform'
import { UserContext } from 'lib/context/app'
import { TaskActionsServiceCommentRequest } from 'pbts/c1/api/task/v1/actions'
import { TaskAuditListRequest } from 'pbts/c1/api/task/v1/audit'
import { ChangeEvent, FC, useCallback, useContext, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { commentOnTaskApi, TaskAuditApi } from 'redux_toolkit/tasks'
import { styles } from './styles'

export const InlineCommentFormV2: FC<{ taskId: string }> = ({ taskId }) => {
    const intl = useIntl()
    const userContext = useContext(UserContext)
    const toast = useAlert()
    const { mutate: addComment } = commentOnTaskApi()
    const { trigger: triggerAuditListApi } = TaskAuditApi.useLazyList()
    const [comment, setComment] = useState<string>('')
    const invalidateList = TaskAuditApi.useInvalidateList()

    const fetchAuditList = useCallback(() => {
        invalidateList()
        triggerAuditListApi(TaskAuditListRequest.create({ taskId }))
    }, [invalidateList, taskId, triggerAuditListApi])

    const handleSubmit = (event: ChangeEvent<HTMLFormElement>): void => {
        event.preventDefault()
        if (!comment.length) {
            return
        }

        addComment(
            TaskActionsServiceCommentRequest.create({ taskId, comment, expandMask: { paths: ['*'] } }),
            MetaSynchronousHeader,
            // TODO Santhosh Call task audit api to get new comments on success after lazy trigger is added
        )
            .then(fetchAuditList)
            .catch((err) => {
                toast(
                    intl.formatMessage(
                        {
                            id: 'task_comment_error',
                            defaultMessage: 'Error adding comment to task. {code}: {msg}',
                        },
                        {
                            code: (err as RpcError).code,
                            msg: (err as RpcError).message,
                        },
                    ),
                    'error',
                )
            })

        setComment('')
    }

    const onChange = (event: ChangeEvent<HTMLInputElement>): void => setComment(event?.target?.value || '')

    return (
        <form onSubmit={handleSubmit}>
            <Box sx={styles.container}>
                <Box display="flex" alignItems="top">
                    <Box mr={2}>
                        <UserAvatar user={userContext.user} />
                    </Box>
                    <Box sx={sxc(styles.inputGrid, { mr: 2 })}>
                        <TextField
                            multiline
                            onChange={onChange}
                            placeholder={intl.formatMessage({
                                id: 'components_tasks_audit_comment_form_label',
                                defaultMessage: 'Add a comment',
                            })}
                            value={comment}
                            helperText={
                                <Typography variant="caption" color="textSecondary">
                                    <FormattedMessage
                                        id="components_tasks_audit_comment_form_markdown_hint"
                                        defaultMessage="Tip: Use Markdown to format your comments"
                                    />
                                </Typography>
                            }
                            InputProps={{
                                inputComponent: TextareaAutosize,
                                inputProps: {
                                    'aria-label': intl.formatMessage({
                                        id: 'components_tasks_audit_comment_form_label',
                                        defaultMessage: 'Add a comment',
                                    }),
                                },
                            }}
                        />
                    </Box>
                    <Box>
                        <C1Button type="submit" color="primary" variant="outlined">
                            <FormattedMessage id="components_tasks_audit_comment_form_btn" defaultMessage="Add comment" />
                        </C1Button>
                    </Box>
                </Box>
            </Box>
        </form>
    )
}
